import React from 'react';
import { StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';
import { useSettingsNavigation } from '../hooks/useSettingsNavigation';
import { ProfileTab } from './ProfileTab';
import StoreTab from './StoreTab';
import BillingTab from './BillingTab';
import CommunicationTab from './CommunicationTab';
import IntegrationsTab from './IntegrationsTab';
import SecurityTab from './SecurityTab';
import AdminTab from './AdminTab';
import { createIconElement } from '@/lib';
import ProfileIcon from '@/assets/icons/ic-profile.svg';
import IntegrationsIcon from '@/assets/icons/ic-integrations.svg';
import WarningCircleIcon from '@/assets/icons/ic-warning-circle.svg';
import BillingIcon from '@/assets/icons/ic-currency-dollar-circle.svg';
import PaymentsIcon from '@/assets/icons/ic-payments.svg';
import EmailNotificationsIcon from '@/assets/icons/ic-email-notifications.svg';
import SecurityIcon from '@/assets/icons/ic-lock.svg';
import AdminIcon from '@/assets/icons/ic-settings.svg';

const TAB_COMPONENTS: Record<string, React.FC> = {
    profile: ProfileTab,
    integrations: IntegrationsTab,
    billing: BillingTab,
    store: StoreTab,
    communication: CommunicationTab,
    security: SecurityTab,
    admin: AdminTab,
};

const TAB_ICONS: Record<string, any> = {
    profile: ProfileIcon,
    integrations: IntegrationsIcon,
    billing: BillingIcon,
    store: PaymentsIcon,
    communication: EmailNotificationsIcon,
    security: SecurityIcon,
    admin: AdminIcon,
};

/**
 * Get the appropriate icon for a tab, showing warning icon if there's an error
 */
const getTabIcon = (tabId: string, hasError?: boolean) => {
    if (hasError) {
        return WarningCircleIcon;
    }
    return TAB_ICONS[tabId];
};

export const SettingsNavigation: React.FC = () => {
    const { activeTab, tabs, handleTabChange } = useSettingsNavigation();

    return (
        <StanTabs value={activeTab} onValueChange={handleTabChange}>
            <div className='mb-4 flex gap-4 max-sm:flex-col sm:mb-6'>
                <StanTabList>
                    {tabs.map(tab => (
                        <StanTab key={tab.id} value={tab.id}>
                            <span className='flex items-center gap-2'>
                                {createIconElement(getTabIcon(tab.id, tab.hasError), {
                                    className: tab.hasError ? 'size-5 text-negative' : 'size-5',
                                })}
                                <span>{tab.title}</span>
                            </span>
                        </StanTab>
                    ))}
                </StanTabList>
            </div>

            {tabs.map(tab => {
                const TabComponent = TAB_COMPONENTS[tab.id];
                if (!TabComponent) return null;
                return (
                    <StanTabContent key={tab.id} value={tab.id} forceMount>
                        <TabComponent />
                    </StanTabContent>
                );
            })}
        </StanTabs>
    );
};
